import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { UserPlus, Key } from "lucide-react";
import { toast } from "sonner";
import USPhoneInput from "./USPhoneInput";
import AddressAutocomplete from "./AddressAutocomplete";

/**
 * Standalone "Create Tenant" — invites a tenant WITHOUT requiring a lease.
 * Useful when a landlord wants to pre-onboard a contact (e.g. a prospective
 * tenant filling out an application) before any lease exists.
 *
 * For the combined tenant + lease onboarding flow, use
 * <AddTenantToPropertyDialog> instead.
 *
 * Props:
 *   - open        boolean
 *   - onOpenChange(bool)
 *   - onCreated() callback fired after the tenant is created so the parent
 *                 can refresh whatever list is showing
 *   - isAdmin     boolean — shows the investor picker for superadmins
 *   - investors   array   — landlord list for the admin picker
 */
export default function CreateTenantDialog({
  open, onOpenChange, onCreated, isAdmin = false, investors = [],
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", phone: "", company: "", landlord_id: "",
    address_full: "", address_line1: "", city: "", state: "", postal_code: "",
  });

  // Reset on open so reusing the dialog doesn't show stale data
  useEffect(() => {
    if (!open) return;
    setForm({
      full_name: "", email: "", password: "", phone: "", company: "", landlord_id: "",
      address_full: "", address_line1: "", city: "", state: "", postal_code: "",
    });
  }, [open]);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: out }));
    toast.success("Temporary password generated — share it with the tenant");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isAdmin && !form.landlord_id) {
      toast.error("Select an investor to assign this tenant to");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // address_full is a UI-only convenience field — strip before POST
      delete payload.address_full;
      if (!isAdmin) delete payload.landlord_id;
      const { data: created } = await api.post("/tenants", payload);
      const emailStatus = created?.welcome_email_status;
      if (created?.welcome_email_sent) {
        toast.success(`${form.full_name} invited — welcome email sent to ${form.email}`);
      } else if (emailStatus === "mocked") {
        toast.message(`${form.full_name} invited (MOCK email mode)`, {
          description: `Share manually · ${form.email} / ${form.password}`,
        });
      } else {
        toast.warning(`${form.full_name} invited — welcome email failed`, {
          description: `Share manually · ${form.email} / ${form.password}`,
        });
      }
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create tenant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-tenant-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-indigo-600" /> Create tenant
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Invite a tenant without linking them to a lease yet — perfect for
            prospective applicants or pre-onboarding.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-2">
          {isAdmin && (
            <Field label="Assign to investor">
              <select
                required
                className="input-clean w-full"
                data-testid="ct-landlord-select"
                value={form.landlord_id}
                onChange={(e) => setForm({ ...form, landlord_id: e.target.value })}
              >
                <option value="">Select investor</option>
                {investors.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.full_name}{l.company ? ` — ${l.company}` : ""} ({l.email})
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Full name">
            <input
              required
              className="input-clean w-full"
              data-testid="ct-name-input"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <input
                required
                type="email"
                className="input-clean w-full"
                data-testid="ct-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <USPhoneInput
                data-testid="ct-phone-input"
                value={form.phone}
                onChange={(digits) => setForm({ ...form, phone: digits })}
              />
            </Field>
          </div>
          <Field label="Company (optional)">
            <input
              className="input-clean w-full"
              data-testid="ct-company-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
          </Field>
          <Field label="Mailing address (optional)">
            <AddressAutocomplete
              value={form.address_full}
              onChange={(v) => setForm({ ...form, address_full: v })}
              onVerified={({ formatted_address, components }) => {
                // Split the verified Google components into the structured
                // backend fields (line1, city, state, postal_code) so the
                // server can reuse them in lease §15 (Notices).
                const street = [components?.street_number, components?.route].filter(Boolean).join(" ").trim();
                const stateCode = components?.administrative_area_level_1_short || components?.administrative_area_level_1 || "";
                setForm((f) => ({
                  ...f,
                  address_full: formatted_address || f.address_full,
                  address_line1: street || formatted_address || "",
                  city: components?.locality || components?.sublocality || "",
                  state: stateCode.length === 2 ? stateCode : "",
                  postal_code: components?.postal_code || "",
                }));
              }}
              placeholder="Start typing — Google will suggest"
              dataTestId="ct-address-input"
            />
          </Field>
          <Field label="Temporary password">
            <div className="flex gap-2">
              <input
                required
                minLength={8}
                type="text"
                className="input-clean flex-1"
                data-testid="ct-pass-input"
                placeholder="min. 8 characters"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button
                type="button"
                onClick={generatePassword}
                data-testid="ct-generate-pass-btn"
                className="btn-outline-dark flex items-center gap-1.5 text-sm shrink-0"
                title="Auto-generate a secure 10-character password"
              >
                <Key className="w-4 h-4" /> Generate
              </button>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Share this password with your tenant once — they'll change it on first login.
            </div>
          </Field>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline-dark"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark disabled:opacity-60"
              data-testid="ct-submit-btn"
              disabled={saving}
            >
              {saving ? "Creating…" : "Create tenant"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
